// PixiJS v8 renderer. Thin shell over the pure visuals cores (spawn/flow/pulse). Bakes the
// luma-key into textures at load (one-time), renders the diatom field as additive "glass"
// sprites with a blurred glow layer for bloom, drifts them with the shared curl-noise flow
// field, pulses them from per-voice envelopes, draws strike ripples, rotates the
// Coscinodiscus as a background lens, and picks via a forgiving bounding circle.
import { Application, Container, Sprite, Texture, BlurFilter, Graphics } from 'pixi.js';
import type { DiatomDescriptor } from '@shared/diatom';
import type { VisualQualityTier } from '@shared/voice';
import { mulberry32 } from '@shared/rng';
import { spawnField, SPAWN_COUNT, type SpawnInstance } from './spawn';
import { smoothstep } from './math';
import { FlowField, breath } from './flowField';
import {
  createPulseState,
  updatePulse,
  strikeFloor,
  pulseScale,
  pulseIntensity,
  pulseTarget,
  type PulseState,
} from './pulse';
import { qualityTier } from './qualityTier';

export interface PickEvent {
  instanceIndex: number;
  diatomId: string;
  /** normalized strike position within the sprite [0,1]. */
  position: { x: number; y: number };
}

interface KeyedAsset {
  texture: Texture;
  halfW: number;
  halfH: number;
}

interface DiatomView {
  inst: SpawnInstance;
  body: Sprite;
  glow: Sprite;
  pulse: PulseState;
  pulseTargetVal: number;
  halfW: number;
  halfH: number;
  /** scale that fits the texture to a viewport-relative target size. */
  fitScale: number;
  /** home position as a fraction of the viewport, for resize reflow. */
  homeNX: number;
  homeNY: number;
  /** current center, velocity (px/s), last-collision time. */
  cx: number;
  cy: number;
  vx: number;
  vy: number;
  lastHit: number;
  /** depth 0 (far: smaller, dimmer, slower, quieter) .. 1 (near). */
  z: number;
}

/** Each diatom targets this fraction of the smaller viewport dimension. */
const TARGET_SIZE_FRACTION = 0.2;

const RIM_BASE = 0.92; // base glass brightness

/** Decode a blob to an ImageBitmap, falling back to <img> on browsers without createImageBitmap. */
async function decodeImage(blob: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(blob);
    } catch {
      /* fall through to <img> */
    }
  }
  const img = new Image();
  img.src = URL.createObjectURL(blob);
  await img.decode();
  return img;
}

/** Load an image as a plain Pixi texture (no keying) — used for the opaque lens. */
async function loadTexture(url: string): Promise<Texture | null> {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return Texture.from(await decodeImage(blob));
  } catch {
    return null;
  }
}

/** Fetch an image, bake luma-key alpha into a canvas texture, and build a pick mask. */
async function loadKeyed(d: DiatomDescriptor, url: string): Promise<KeyedAsset> {
  const res = await fetch(url);
  const blob = await res.blob();
  const bmp = await decodeImage(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(bmp, 0, 0);
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = img.data;
  for (let i = 0; i < data.length; i += 4) {
    const luma = (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
    const a = smoothstep(d.lumaLo, d.lumaHi, luma);
    // keep grayscale silica detail; lift toward cool glass white
    const v = Math.min(255, 180 + luma * 110);
    data[i] = v * 0.95;
    data[i + 1] = v * 0.98;
    data[i + 2] = v;
    data[i + 3] = Math.round(a * 255);
  }
  ctx.putImageData(img, 0, 0);

  return {
    texture: Texture.from(canvas),
    halfW: canvas.width / 2,
    halfH: canvas.height / 2,
  };
}

export interface RendererOptions {
  tier: VisualQualityTier;
  reducedMotion: boolean;
  seed?: number;
}

export class Renderer {
  private app = new Application();
  private bodyLayer = new Container();
  private glowLayer = new Container();
  private lensLayer = new Container();
  private rippleGfx = new Graphics();
  private views: DiatomView[] = [];
  private flow: FlowField;
  private ripples: Array<{ x: number; y: number; age: number; speed: number; energy: number }> = [];
  private elapsed = 0;
  private space = 0.6;
  private brightness = 0.4;
  private pickCb: ((e: PickEvent) => void) | null = null;
  private collisionCb: ((a: number, b: number, energy: number) => void) | null = null;
  private contacts = new Set<number>();
  private motionSpeed = 1;
  private motionAmp = 1;
  private driftScale = 1; // autonomous curl-noise drift gate (0 in Playable mode)
  // device tilt (gravity, px/s^2) + a seeded RNG for shake-scatter impulses
  private gravX = 0;
  private gravY = 0;
  private motionRng = mulberry32(0x5ca77e1);
  // drag/throw state
  private dragView: DiatomView | null = null;
  private dragMoved = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private ptrX = 0;
  private ptrY = 0;
  private ptrVX = 0;
  private ptrVY = 0;
  private ptrT = 0;
  // cursor-as-current: the live pointer (even when not dragging) stirs nearby diatoms
  private cursorX = -1;
  private cursorY = -1;
  private cursorVX = 0;
  private cursorVY = 0;
  private cursorMoveT = 0;
  private lensSprite: Sprite | null = null;
  private readonly cfg;

  constructor(private readonly opts: RendererOptions) {
    this.cfg = qualityTier(opts.tier, opts.reducedMotion);
    this.flow = new FlowField({ seed: opts.seed ?? 0xf100, reducedMotion: opts.reducedMotion });
  }

  async init(parent: HTMLElement, diatoms: DiatomDescriptor[]): Promise<void> {
    await this.app.init({
      resizeTo: parent,
      background: 0x04070b,
      antialias: true,
      preference: 'webgl',
      powerPreference: 'high-performance',
      // Cap resolution: dpr=3 phones/iPads would otherwise use ~9x texture memory (OOM risk).
      resolution: Math.min(typeof devicePixelRatio === 'number' ? devicePixelRatio : 1, 2),
      autoDensity: true,
    });
    parent.appendChild(this.app.canvas);
    // Keyboard focus + a label so the field isn't an opaque element to assistive tech.
    this.app.canvas.tabIndex = 0;
    this.app.canvas.setAttribute('role', 'application');
    this.app.canvas.setAttribute(
      'aria-label',
      'Diatom field — drifting resonant shells. Tap or click a diatom to strike it.',
    );

    const stage = this.app.stage;
    this.glowLayer.filters = [new BlurFilter({ strength: 14, quality: 4 })];
    // depth ordering: far diatoms (low z) draw behind near ones
    this.bodyLayer.sortableChildren = true;
    this.glowLayer.sortableChildren = true;
    stage.addChild(this.lensLayer, this.glowLayer, this.bodyLayer, this.rippleGfx);

    const lens = diatoms.find((d) => d.backgroundLens);
    const bodies = diatoms.filter((d) => !d.backgroundLens);

    // Load textures (keyed) for body diatoms + the lens.
    const assets = new Map<string, KeyedAsset>();
    await Promise.all(
      bodies.map(async (d) => assets.set(d.id, await loadKeyed(d, `/diatoms/${d.id}.webp`))),
    );

    // Spawn the field across the viewport.
    const count = Math.min(SPAWN_COUNT[this.opts.tier], bodies.length * 2, 22);
    const w = this.app.renderer.width;
    const h = this.app.renderer.height;
    const ids = bodies.map((b) => b.id);
    const instances = spawnField(this.opts.seed ?? 0x5eed, count, w, h, ids);
    const depthRng = mulberry32(((this.opts.seed ?? 0x5eed) ^ 0xd3017) >>> 0);

    for (const inst of instances) {
      const asset = assets.get(inst.imageId);
      if (!asset) continue;
      const z = depthRng();
      const body = new Sprite(asset.texture);
      body.anchor.set(0.5);
      body.blendMode = 'add';
      body.zIndex = Math.round(z * 1000);
      const glow = new Sprite(asset.texture);
      glow.anchor.set(0.5);
      glow.blendMode = 'add';
      glow.alpha = 0.5;
      glow.zIndex = body.zIndex;
      this.bodyLayer.addChild(body);
      this.glowLayer.addChild(glow);
      const target = Math.min(w, h) * TARGET_SIZE_FRACTION;
      const fitScale = target / (Math.max(asset.halfW, asset.halfH) * 2);
      this.views.push({
        inst,
        body,
        glow,
        pulse: createPulseState(),
        pulseTargetVal: 0,
        halfW: asset.halfW,
        halfH: asset.halfH,
        fitScale,
        homeNX: inst.x / w,
        homeNY: inst.y / h,
        cx: inst.x,
        cy: inst.y,
        vx: 0,
        vy: 0,
        lastHit: -1,
        z,
      });
    }

    // Background lens (Coscinodiscus): opaque -> low-opacity rotating "lens", not keyed.
    if (lens) {
      const tex = await loadTexture(`/diatoms/${lens.id}.webp`);
      if (tex) {
        const s = new Sprite(tex);
        s.anchor.set(0.5);
        s.x = w / 2;
        s.y = h / 2;
        const scale = (Math.min(w, h) * 1.25) / s.height;
        s.scale.set(scale);
        s.alpha = 0.16;
        s.tint = 0x3a4658;
        this.lensLayer.addChild(s);
        this.lensSprite = s;
      }
    }

    // Named handler refs so destroy() can remove them for a clean teardown.
    this.app.canvas.addEventListener('pointerdown', this.onPointerDownH, { passive: true });
    window.addEventListener('pointermove', this.onPointerMoveH, { passive: true });
    window.addEventListener('pointerup', this.onPointerUpH, { passive: true });
    window.addEventListener('pointercancel', this.onPointerCancelH, { passive: true });
    this.app.renderer.on('resize', this.onResizeH);
    window.addEventListener('resize', this.onResizeH);
    this.app.ticker.add(this.onTick);
  }

  // Bound event handlers (stable refs for add/removeEventListener).
  private readonly onPointerDownH = (ev: PointerEvent): void => this.onPointerDown(ev);
  private readonly onPointerMoveH = (ev: PointerEvent): void => this.onPointerMove(ev);
  private readonly onPointerUpH = (ev: PointerEvent): void => this.onPointerUp(ev);
  private readonly onPointerCancelH = (): void => this.endDrag(false);
  private readonly onResizeH = (): void => this.resize();
  private readonly onTick = (ticker: { deltaMS: number }): void => this.frame(ticker.deltaMS / 1000);

  private destroyed = false;

  /** Tear down: remove listeners, stop the ticker, destroy the Pixi app + GPU resources. Idempotent. */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    // Window listeners don't depend on the Pixi app — always removable.
    window.removeEventListener('pointermove', this.onPointerMoveH);
    window.removeEventListener('pointerup', this.onPointerUpH);
    window.removeEventListener('pointercancel', this.onPointerCancelH);
    window.removeEventListener('resize', this.onResizeH);
    this.pickCb = null;
    this.collisionCb = null;
    // The canvas getter throws if the Pixi app is already torn down — guard it.
    try {
      this.app.canvas.removeEventListener('pointerdown', this.onPointerDownH);
    } catch {
      /* app already destroyed */
    }
    try {
      this.app.destroy(true, { children: true });
    } catch {
      /* already torn down */
    }
  }

  onPick(cb: (e: PickEvent) => void): void {
    this.pickCb = cb;
  }

  /** Fired when two drifting diatoms collide: (instanceA, instanceB, impactEnergy 0..1). */
  onCollision(cb: (a: number, b: number, energy: number) => void): void {
    this.collisionCb = cb;
  }

  /** Per-preset drift character: speed = how fast the flow churns, amp = how far they roam. */
  setMotion(speed: number, amp: number): void {
    this.motionSpeed = speed;
    this.motionAmp = amp;
  }

  /**
   * Scale the autonomous curl-noise drift (1 = full, 0 = off). Playable mode sets this to 0 so the
   * field settles and waits — user stirring/throwing/tapping still moves and rings diatoms (those
   * forces are separate), but the water stops drifting (and self-colliding) on its own.
   */
  setDrift(scale: number): void {
    this.driftScale = Math.max(0, scale);
  }

  /** Device-tilt "gravity" (px/s^2) added to every drifting diatom. (0,0) = phone level. */
  setGravity(gx: number, gy: number): void {
    this.gravX = gx;
    this.gravY = gy;
  }

  /** Shake: kick every (non-dragged) diatom with a random impulse (px/s) — scatters the field. */
  scatter(strength: number): void {
    for (const view of this.views) {
      if (view === this.dragView) continue;
      view.vx += (this.motionRng() * 2 - 1) * strength;
      view.vy += (this.motionRng() * 2 - 1) * strength;
    }
  }

  /** Map an engine voice's measured RMS onto a sprite's pulse (called per frame by the app). */
  setVoicePulse(instanceIndex: number, rms: number): void {
    const view = this.views[instanceIndex];
    if (view) view.pulseTargetVal = Math.max(view.pulseTargetVal, pulseTarget(rms));
  }

  /** Visual response to a strike: synchronous flash + ripple ring. */
  applyStrike(instanceIndex: number, energy: number, fundamentalHz: number): void {
    const view = this.views[instanceIndex];
    if (!view) return;
    strikeFloor(view.pulse, energy);
    if (this.ripples.length < this.cfg.ripplePoolSize) {
      this.ripples.push({
        x: view.body.x,
        y: view.body.y,
        age: 0,
        speed: 80 + fundamentalHz * 0.15,
        energy,
      });
    }
  }

  /** Driven by ParameterState each frame. */
  setGlobals(space: number, brightness: number): void {
    this.space = space;
    this.brightness = brightness;
  }

  /** Index -> diatom id, for the app to map picks/voices. */
  diatomIdAt(index: number): string | undefined {
    return this.views[index]?.inst.imageId;
  }

  instancePosition(index: number): { x: number; y: number } | undefined {
    const v = this.views[index];
    return v ? { x: v.body.x, y: v.body.y } : undefined;
  }

  /** All sprite instance indices currently showing the given diatom id. */
  instancesForDiatom(id: string): number[] {
    const out: number[] = [];
    for (let i = 0; i < this.views.length; i++) {
      if (this.views[i].inst.imageId === id) out.push(i);
    }
    return out;
  }

  get viewport(): { w: number; h: number } {
    return { w: this.app.renderer.width, h: this.app.renderer.height };
  }

  private canvasXY(ev: PointerEvent): { x: number; y: number } {
    // Pointer events are in CSS px; sprite positions are in renderer px (CSS × resolution).
    // Scale by the renderer/CSS ratio so picking & drag align on any devicePixelRatio.
    const rect = this.app.canvas.getBoundingClientRect();
    const sx = rect.width > 0 ? this.app.renderer.width / rect.width : 1;
    const sy = rect.height > 0 ? this.app.renderer.height / rect.height : 1;
    return { x: (ev.clientX - rect.left) * sx, y: (ev.clientY - rect.top) * sy };
  }

  private onPointerDown(ev: PointerEvent): void {
    const { x, y } = this.canvasXY(ev);
    // Forgiving bounding-circle pick (grab the whole diatom incl. its dark hub), topmost first.
    let hit: number | null = null;
    for (let i = this.views.length - 1; i >= 0; i--) {
      const v = this.views[i];
      const pickR = Math.max(v.halfW, v.halfH) * v.inst.baseScale * v.fitScale * (0.6 + 0.55 * v.z) * 0.62;
      if (Math.hypot(x - v.cx, y - v.cy) <= pickR) {
        hit = i;
        break;
      }
    }
    if (hit === null) return;
    // Begin a potential drag. A tap (no movement) fires a strike on release; a drag throws.
    this.dragView = this.views[hit];
    this.dragMoved = false;
    this.dragStartX = x;
    this.dragStartY = y;
    this.ptrX = x;
    this.ptrY = y;
    this.ptrVX = 0;
    this.ptrVY = 0;
    this.ptrT = performance.now();
  }

  private onPointerMove(ev: PointerEvent): void {
    const { x, y } = this.canvasXY(ev);
    const now = performance.now();
    // After an idle gap (or first move), don't infer a velocity from a stale sample.
    const gap = now - this.ptrT;
    const dt = Math.max(0.001, gap / 1000);
    if (gap > 100) {
      this.ptrVX = 0;
      this.ptrVY = 0;
    } else {
      // smoothed pointer velocity (used by throws AND the cursor-current stir)
      this.ptrVX = 0.6 * this.ptrVX + 0.4 * ((x - this.ptrX) / dt);
      this.ptrVY = 0.6 * this.ptrVY + 0.4 * ((y - this.ptrY) / dt);
    }
    this.ptrX = x;
    this.ptrY = y;
    this.ptrT = now;
    // cursor-as-current: remember the live pointer + its motion so frame() can stir nearby diatoms
    this.cursorX = x;
    this.cursorY = y;
    this.cursorVX = this.ptrVX;
    this.cursorVY = this.ptrVY;
    this.cursorMoveT = now;

    if (!this.dragView) return;
    if (Math.hypot(x - this.dragStartX, y - this.dragStartY) > 8) this.dragMoved = true;
    // the diatom follows the cursor; its velocity tracks the drag (drives collisions + throw)
    this.dragView.cx = x;
    this.dragView.cy = y;
    this.dragView.vx = this.ptrVX;
    this.dragView.vy = this.ptrVY;
  }

  private onPointerUp(_ev: PointerEvent): void {
    this.endDrag(true);
  }

  private endDrag(allowAction: boolean): void {
    const view = this.dragView;
    if (!view) return;
    this.dragView = null;
    if (!allowAction) return;
    if (this.dragMoved) {
      // throw: impart the pointer velocity (clamped)
      const maxThrow = 900;
      const sp = Math.hypot(this.ptrVX, this.ptrVY);
      const k = sp > maxThrow ? maxThrow / sp : 1;
      view.vx = this.ptrVX * k;
      view.vy = this.ptrVY * k;
    } else if (this.pickCb) {
      // tap: strike
      const idx = this.views.indexOf(view);
      this.pickCb({ instanceIndex: idx, diatomId: view.inst.imageId, position: { x: 0.5, y: 0.5 } });
    }
  }

  private frame(dt: number): void {
    this.elapsed += dt;
    this.flow.evolve(dt * this.motionSpeed, this.space);
    const warmCool = this.brightness;
    const w = this.app.renderer.width;
    const h = this.app.renderer.height;
    // Drifting "tank" model: the curl-noise field is a force, diatoms carry momentum,
    // damp gently, bounce off the edges, and rebound off each other (collisions = sound).
    const reduced = this.opts.reducedMotion;
    const force = 70 * this.motionAmp;
    const maxV = 130 * this.motionAmp;
    const drag = Math.exp(-dt / 3.5);
    // cursor-as-current: stir diatoms within reach of a recently-moved pointer (off if reduced motion)
    const stirFresh = !reduced && performance.now() - this.cursorMoveT < 180;
    const stirR = Math.min(w, h) * 0.25;
    const stirR2 = stirR * stirR;

    for (const view of this.views) {
      const { inst } = view;
      const r = Math.max(view.halfW, view.halfH) * inst.baseScale * view.fitScale * (0.6 + 0.55 * view.z) * 0.5;
      const isDrag = view === this.dragView;

      if (isDrag) {
        // pointer owns cx/cy and vx/vy (set in onPointerMove); skip physics
      } else if (!reduced) {
        // parallax: far diatoms (low z) drift slower
        const par = 0.6 + 0.5 * view.z;
        const f = this.flow.sample(view.cx, view.cy);
        const df = force * this.driftScale;
        view.vx = (view.vx + f.vx * df * par * dt) * drag;
        view.vy = (view.vy + f.vy * df * par * dt) * drag;
        // device tilt: gravity pulls diatoms toward the low edge (parallax-scaled)
        view.vx += this.gravX * par * dt;
        view.vy += this.gravY * par * dt;
        // very weak pull home keeps the field from clumping into a corner
        view.vx += (inst.x - view.cx) * 0.04 * dt;
        view.vy += (inst.y - view.cy) * 0.04 * dt;
        // cursor-as-current: a moving pointer drags nearby diatoms along its motion + parts them
        // around it, so stirring the water sets diatoms drifting (and colliding -> sounding).
        if (stirFresh) {
          const dx = view.cx - this.cursorX;
          const dy = view.cy - this.cursorY;
          const d2 = dx * dx + dy * dy;
          if (d2 < stirR2) {
            const dist = Math.sqrt(d2);
            const fall = 1 - dist / stirR; // 1 at the cursor, 0 at the edge of reach
            const k = fall * fall * dt;
            view.vx += (this.cursorVX * 0.4 + (dx / (dist + 1)) * 40 * this.motionAmp) * k;
            view.vy += (this.cursorVY * 0.4 + (dy / (dist + 1)) * 40 * this.motionAmp) * k;
          }
        }
        const vmax = maxV * par;
        const sp2 = Math.hypot(view.vx, view.vy);
        if (sp2 > vmax) {
          view.vx *= vmax / sp2;
          view.vy *= vmax / sp2;
        }
        view.cx += view.vx * dt;
        view.cy += view.vy * dt;
        // bounce off viewport edges
        if (view.cx < r) {
          view.cx = r;
          view.vx = Math.abs(view.vx) * 0.7;
        } else if (view.cx > w - r) {
          view.cx = w - r;
          view.vx = -Math.abs(view.vx) * 0.7;
        }
        if (view.cy < r) {
          view.cy = r;
          view.vy = Math.abs(view.vy) * 0.7;
        } else if (view.cy > h - r) {
          view.cy = h - r;
          view.vy = -Math.abs(view.vy) * 0.7;
        }
      } else {
        view.vx = 0;
        view.vy = 0;
      }

      // pulse toward its current target, then decay target toward 0
      const p = updatePulse(view.pulse, view.pulseTargetVal, dt);
      view.pulseTargetVal *= Math.exp(-dt / 0.5);

      const br = breath(this.elapsed, inst.breathHz, inst.breathPhase, this.opts.reducedMotion);
      // depth: far diatoms are smaller and dimmer
      const depthScale = 0.6 + 0.55 * view.z;
      const depthAlpha = 0.45 + 0.55 * view.z;
      const s = pulseScale(inst.baseScale * view.fitScale * depthScale, p, br);
      const intensity = pulseIntensity(p);

      // body + glow share position/scale; written explicitly to avoid a per-frame array allocation.
      const sx = s * inst.mirror;
      view.body.x = view.cx;
      view.body.y = view.cy;
      view.body.rotation = inst.baseRotation;
      view.body.scale.set(sx, s);
      view.glow.x = view.cx;
      view.glow.y = view.cy;
      view.glow.rotation = inst.baseRotation;
      view.glow.scale.set(sx, s);
      // brightness/pulse -> tint + alpha (depth-attenuated)
      const tint = this.coolWarmTint(warmCool, p);
      view.body.tint = tint;
      view.glow.tint = tint;
      view.body.alpha = Math.min(1, RIM_BASE * intensity * depthAlpha);
      view.glow.alpha = Math.min(0.9, (0.35 + 0.5 * p) * depthAlpha);
    }

    if (this.lensSprite && !this.opts.reducedMotion) {
      this.lensSprite.rotation += dt * 0.004;
      this.lensSprite.alpha = 0.14 + 0.06 * this.space; // a touch more visible at high space
    }

    this.collisionPass(dt);
    this.drawRipples(dt);
  }

  /** Detect diatoms drifting into contact and emit collision events (movement -> sound). */
  private collisionPass(dt: number): void {
    if (!this.collisionCb || dt <= 0) return;
    const radius = (v: DiatomView) =>
      Math.max(v.halfW, v.halfH) * v.inst.baseScale * v.fitScale * (0.6 + 0.55 * v.z) * 0.5;
    const n = this.views.length;
    for (let i = 0; i < n; i++) {
      const a = this.views[i];
      const ra = radius(a);
      for (let j = i + 1; j < n; j++) {
        const b = this.views[j];
        const rsum = ra + radius(b);
        const dx = a.cx - b.cx;
        const dy = a.cy - b.cy;
        const dist = Math.hypot(dx, dy);
        const key = i * n + j; // integer pair key (no per-frame string allocation)
        if (dist < rsum) {
          if (!this.contacts.has(key)) {
            this.contacts.add(key);
            // relative impact speed (px/s) from the bodies' velocities (physics or drag)
            const relSpeed = Math.hypot(a.vx - b.vx, a.vy - b.vy);
            // depth cue: collisions among far diatoms ring quieter
            const depth = 0.55 + 0.45 * Math.min(a.z, b.z);
            const energy = Math.max(0.1, Math.min(0.9, (relSpeed / 350 + 0.12) * depth));
            // per-sprite cooldown so a slow graze doesn't machine-gun
            if (this.elapsed - a.lastHit > 0.12 && this.elapsed - b.lastHit > 0.12) {
              a.lastHit = this.elapsed;
              b.lastHit = this.elapsed;
              this.collisionCb(i, j, energy);
              strikeFloor(a.pulse, energy);
              strikeFloor(b.pulse, energy);
            }
            // elastic rebound + separation so they bounce apart instead of sticking
            const inv = dist > 1e-3 ? 1 / dist : 0;
            const nx = dx * inv;
            const ny = dy * inv;
            const vn = (a.vx - b.vx) * nx + (a.vy - b.vy) * ny;
            if (vn < 0) {
              a.vx -= vn * nx;
              a.vy -= vn * ny;
              b.vx += vn * nx;
              b.vy += vn * ny;
            }
            const overlap = (rsum - dist) * 0.5;
            a.cx += nx * overlap;
            a.cy += ny * overlap;
            b.cx -= nx * overlap;
            b.cy -= ny * overlap;
          }
        } else if (dist > rsum * 1.18) {
          // hysteresis: only clear contact once well separated
          this.contacts.delete(key);
        }
      }
    }
  }

  private drawRipples(dt: number): void {
    this.rippleGfx.clear();
    if (this.ripples.length === 0) return; // no per-frame work / allocation when idle
    const limit = Math.max(this.app.renderer.width, this.app.renderer.height);
    let w = 0; // compact survivors in place (no new array each frame)
    for (let i = 0; i < this.ripples.length; i++) {
      const r = this.ripples[i];
      r.age += dt;
      const radius = r.speed * r.age;
      const life = Math.max(0, 1 - r.age / 2.2);
      if (life <= 0 || radius > limit) continue;
      this.rippleGfx
        .circle(r.x, r.y, radius)
        .stroke({ width: 1.5, color: 0x9fc4ff, alpha: life * 0.4 * (0.4 + r.energy) });
      this.ripples[w++] = r;
    }
    this.ripples.length = w;
  }

  private coolWarmTint(brightness: number, pulse: number): number {
    // neutral white nudged subtly cool (low brightness) to warm (high brightness)
    const w = Math.max(-1, Math.min(1, brightness * 1.4 - 0.7 + 0.2 * pulse));
    const r = 0.9 + 0.1 * Math.max(0, w);
    const g = 0.95 + 0.04 * Math.abs(w);
    const b = 0.95 + 0.05 * Math.max(0, -w);
    const to8 = (x: number) => Math.min(255, Math.round(x * 255));
    return (to8(r) << 16) | (to8(g) << 8) | to8(b);
  }

  resize(): void {
    // resizeTo handles the canvas size; recompute viewport-relative fit + reflow positions.
    const w = this.app.renderer.width;
    const h = this.app.renderer.height;
    const target = Math.min(w, h) * TARGET_SIZE_FRACTION;
    for (const v of this.views) {
      v.fitScale = target / (Math.max(v.halfW, v.halfH) * 2);
      v.inst.x = v.homeNX * w;
      v.inst.y = v.homeNY * h;
    }
    if (this.lensSprite) {
      this.lensSprite.x = w / 2;
      this.lensSprite.y = h / 2;
      const scale = (Math.min(w, h) * 1.25) / this.lensSprite.texture.height;
      this.lensSprite.scale.set(scale);
    }
  }

  get canvas(): HTMLCanvasElement {
    return this.app.canvas;
  }
}
